import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { AlertCircle, Calendar, Pencil, Plus, Repeat, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  ActionButton,
  ContextHero,
  DataCard,
  DateBadge,
  EmptyStateModern,
  KpiCard,
  MoneyAmount,
  PageContainer,
  SectionTitle,
  SkeletonShimmer,
  StatusBadge,
} from '@/components/design-system';
import { DangerousConfirmDialog } from '@/components/ui/dangerous-confirm-dialog';
import {
  useChargesRecurrentes,
  useDeleteChargeRecurrente,
  useGenererEcheances12Mois,
  type ChargeRecurrente,
} from '@/hooks/queries/useCaisseGroupe';
import { ChargeRecurrenteFormDialog } from './ChargeRecurrenteFormDialog';

export function ChargesRecurrentesTab(): JSX.Element {
  const { data: charges = [], isLoading } = useChargesRecurrentes({});
  const remove = useDeleteChargeRecurrente();
  const generer = useGenererEcheances12Mois();
  const [editing, setEditing] = useState<ChargeRecurrente | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<ChargeRecurrente | null>(null);

  const totalMensuel = useMemo(() => {
    return charges
      .filter((c) => c.actif)
      .reduce((s, c) => {
        const mult: Record<string, number> = {
          mensuelle: 1,
          trimestrielle: 1 / 3,
          semestrielle: 1 / 6,
          annuelle: 1 / 12,
        };
        return s + Number(c.montant) * (mult[c.periodicite] ?? 1);
      }, 0);
  }, [charges]);

  const prochaine = useMemo(() => {
    const actives = charges.filter((c) => c.actif && c.prochaine_echeance);
    if (actives.length === 0) return null;
    return actives
      .slice()
      .sort(
        (a, b) =>
          new Date(a.prochaine_echeance!).getTime() - new Date(b.prochaine_echeance!).getTime(),
      )[0];
  }, [charges]);

  const joursAvant = prochaine
    ? Math.ceil((new Date(prochaine.prochaine_echeance!).getTime() - Date.now()) / 86400000)
    : 0;

  const handleGenerer = async (): Promise<void> => {
    const r = await generer.mutateAsync();
    if (r.errors.length === 0) toast.success(`${r.created} échéances créées sur 12 mois`);
    else toast.warning(`${r.created} OK · ${r.errors.length} échec(s)`);
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        <ContextHero
          variant="finances"
          icon={<Repeat className="h-5 w-5" />}
          title="Charges récurrentes prévisionnelles"
          subtitle="Loyer, salaires, électricité — alloués par activité avec presets KOST par étage. Génération auto des échéances 12 mois."
          actions={
            <div className="flex gap-2">
              <ActionButton
                variant="secondary"
                size="sm"
                loading={generer.isPending}
                onClick={() => void handleGenerer()}
              >
                Générer 12 mois
              </ActionButton>
              <ActionButton
                variant="gradient"
                module="finances"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                Nouvelle charge
              </ActionButton>
            </div>
          }
        />

        <div className="grid gap-3 sm:grid-cols-3">
          <KpiCard
            variant="finances"
            label="Charges mensuelles équiv."
            value={<MoneyAmount amount={totalMensuel} variant="inline" />}
            icon={<Repeat className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="Prochaine échéance"
            value={prochaine ? `J${joursAvant >= 0 ? '−' : '+'}${Math.abs(joursAvant)}` : '—'}
            hint={prochaine?.libelle}
            icon={<AlertCircle className="h-4 w-4" />}
            loading={isLoading}
          />
          <KpiCard
            variant="finances"
            label="Charges actives"
            value={charges.filter((c) => c.actif).length}
            icon={<Calendar className="h-4 w-4" />}
            loading={isLoading}
          />
        </div>

        <SectionTitle>
          {charges.length} charge{charges.length > 1 ? 's' : ''}
        </SectionTitle>

        {isLoading ? (
          <SkeletonShimmer variant="table" count={3} />
        ) : charges.length === 0 ? (
          <EmptyStateModern
            variant="finances"
            icon={<Repeat className="h-7 w-7" />}
            title="Aucune charge récurrente"
            description="Configurez votre loyer, vos salaires, votre électricité avec allocation par activité — la projection cash-flow s'enrichit automatiquement."
            ctaLabel="Nouvelle charge récurrente"
            onCtaClick={() => {
              setEditing(null);
              setDialogOpen(true);
            }}
          />
        ) : (
          <DataCard>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-[10px] uppercase tracking-widest text-slate-400">
                  <tr>
                    <th className="py-2">Libellé</th>
                    <th className="py-2">Type</th>
                    <th className="py-2 text-right">Montant</th>
                    <th className="py-2">Périodicité</th>
                    <th className="py-2">Prochaine éch.</th>
                    <th className="py-2">Actif</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {charges.map((c, i) => (
                    <motion.tr
                      key={c.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i, 8) * 0.02 }}
                      className="border-t border-slate-100"
                    >
                      <td className="py-2">
                        <div className="font-semibold text-slate-800">{c.libelle}</div>
                        {c.description && (
                          <div className="line-clamp-1 text-[10px] text-slate-500">
                            {c.description}
                          </div>
                        )}
                      </td>
                      <td className="py-2">
                        <StatusBadge
                          status={c.allocation_type === 'groupe' ? 'planifie' : 'fait'}
                          label={c.allocation_type === 'groupe' ? 'Groupe' : 'Spécifique'}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <MoneyAmount amount={c.montant} variant="inline" />
                      </td>
                      <td className="py-2 capitalize text-slate-600">{c.periodicite}</td>
                      <td className="py-2">
                        <DateBadge date={c.prochaine_echeance} />
                      </td>
                      <td className="py-2">
                        <StatusBadge
                          status={c.actif ? 'paye' : 'annule'}
                          label={c.actif ? 'Actif' : 'Inactif'}
                        />
                      </td>
                      <td className="py-2 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            onClick={() => {
                              setEditing(c);
                              setDialogOpen(true);
                            }}
                            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                            aria-label="Modifier"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => setConfirmDelete(c)}
                            className="rounded p-1 text-rose-500 hover:bg-rose-50"
                            aria-label="Supprimer"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </DataCard>
        )}
      </div>

      <ChargeRecurrenteFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        onSaved={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <DangerousConfirmDialog
        open={!!confirmDelete}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={`Supprimer la charge « ${confirmDelete?.libelle} » ?`}
        description="Cela ne supprime pas les échéances déjà générées. Action irréversible."
        consequences={[
          `${confirmDelete?.libelle ?? ''} supprimée du référentiel`,
          'Échéances futures non générées',
        ]}
        challenge="SUPPRIMER"
        loading={remove.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          await remove.mutateAsync(confirmDelete.id);
          toast.success('Charge supprimée');
          setConfirmDelete(null);
        }}
      />
    </PageContainer>
  );
}
